/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import isNil from 'lodash/isNil';
import isObjectLike from 'lodash/isObjectLike';
import isString from 'lodash/isString';

import CommunicatorStretch from '../../communicator/stretch/communicator-stretch';
import CommunicatorUtility from '../../communicator/utility/communicator-utility';
import { isISOString } from '../../iso-string';

/**
 * Komunikátor médií pro Ki-Wi Server.
 */
class MediumCommunicator {
  /**
   * Naparsuje média z JSON.
   * @param {Object[]} jsonArray Část odpovědi na obsah zařízení s polem médií.
   * @returns {import('../medium-attrs').MediumAttrs[]}
   */
  static parseMany(jsonArray) {
    return jsonArray.map(this.parseOne);
  }

  /**
   * Naparsuje médium z JSON.
   * @param {Object} json Část odpovědi na obsah zařízení s jedním médiem.
   * @returns {import('../medium-attrs').MediumAttrs}
   */
  static parseOne(json) {
    try {
      const isCreatedISOString = isISOString(
        CommunicatorUtility.parseStringOrNull(json.created)
      );
      return {
        asset: undefined,
        backgroundColorAlpha:
          CommunicatorUtility.parseBackgroundColorAlphaPercentage(
            json.backgroundColorAlphaPercentage
          ),
        // TODO #555: Udělat / předělat všude na parseStringOrUndefined.
        checksum: CommunicatorUtility.parseStringOrNull(json.md5) ?? undefined,
        created: isCreatedISOString ? json.created : undefined,
        id: json.id,
        name: CommunicatorUtility.parseStringRequired(json.name),
        params: MediumCommunicator.parseParams(json.params),
        playDuration: CommunicatorUtility.parseTime(json.playLength.timeLength),
        onlineUrl: json.onlineUrl
          ? CommunicatorUtility.parseStringUrl(json.onlineUrl)
          : undefined,
        // U média je stretchEnum volitelné, ale např. u zařízení povinné.
        stretchEnum: CommunicatorStretch.parse(json.stretching),
        type: CommunicatorUtility.parseStringRequired(json.type),
        uri: json.uri,
        // TODO: Správně by se mělo používat `CommunicatorUtility.parseStringUrl(json.url)`,
        // ale historicky Ki-Wi Server (resp. uživatel) může poslat v URL nevalidní hodnoty.
        // Proto se nyní validita URL při parsování nekontroluje, ale případně nevalidní URL
        // se předává dál, aby to případně spadlo v runtime v místě použití, tj.
        // AssetDownloader.download nebo ContentInfoParser.linkAssetsToMedia.
        // url: CommunicatorUtility.parseStringUrl(json.url),
        url: CommunicatorUtility.parseStringRequired(json.url),
        volume: CommunicatorUtility.parsePercentage(json.volume)
      };
    } catch (error) {
      throw new TypeError(`parsing medium ${JSON.stringify(json)}: ${error}`);
    }
  }

  // TODO #565: Možná trochu bug v tom, že se neparsují a neošetřují
  // zanořené hodnoty, které chodí typicky pro médium typu kiosk.
  // Ale je otázka, zda by se i pro zanořené stringové hodnoty měl
  // taky dělat escaping.
  /**
   * Zpracuje parametry média.
   * @param {{ [key: string]: any } | null | undefined} json Část odpovědi na obsah zařízení s parametry média.
   * @private
   * @returns {{ [key: string]: string | number | boolean | null } | undefined} Parametry ke vložení do datového skladu.
   */
  static parseParams(json) {
    // Asi zbytečný test, jelikož z Ki-Wi Serveru chodí klíč vždy a je vždy objekt
    // a z 99% neprázdný s nějakým klíčem.
    // A pokud ne, ta se vzhledem k nevolitelnosti vrátí prázdný objekt než null/undefined.
    if (isNil(json) || !isObjectLike(json)) {
      return undefined;
    }
    return Object.fromEntries(
      Object.entries(json).reduce((acc, [key, value]) => {
        if (isString(value)) {
          // Dekódování URI, které server provádí.
          // Lze použít jednotné sestavení URI, které automaticky provádí encode.
          // Ušetří místo v datovém skladu.
          acc.push([key, decodeURIComponent(value)]);
        } else if (!isObjectLike(value)) {
          // Zbytek primitivních typů se zkopíruje beze změny.
          acc.push([key, value]);
        }
        return acc;
      }, /** @type {[ key: string, value: string | number | boolean | null ][]} */ ([]))
    );
  }
}

export default MediumCommunicator;
